import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useState, useEffect } from "react";
import { CartHeader } from "./cart/CartHeader";
import { CartContent } from "./cart/CartContent";
import { CartFooter } from "./cart/CartFooter";
import { useNewCart } from "@/hooks/useNewCart";
import { useCartValidation } from "./hooks/useCartValidation";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import jsPDF from "jspdf";
import 'jspdf-autotable';
interface CartDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  refetch: () => void;
}
export const CartDialog = ({
  open,
  onOpenChange,
  refetch
}: CartDialogProps) => {
  const navigate = useNavigate();
  const [digitalSignature, setDigitalSignature] = useState("");
  const [isCredit, setIsCredit] = useState(false);
  const [partialPayment, setPartialPayment] = useState("");
  const [paymentDueDate, setPaymentDueDate] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerContact, setCustomerContact] = useState("");
  const [customerAddress, setCustomerAddress] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [isDelivery, setIsDelivery] = useState(false);
  const [selectedZone, setSelectedZone] = useState("");
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [deliveryPaymentType, setDeliveryPaymentType] = useState("");
  const [customDeliveryAmount, setCustomDeliveryAmount] = useState("");
  const [selectedDriver, setSelectedDriver] = useState("");
  const [customDriverName, setCustomDriverName] = useState("");
  const [generateInvoice, setGenerateInvoice] = useState(false);
  const [includeCustomerInfo, setIncludeCustomerInfo] = useState(false);
  const {
    cartItems,
    isLoading,
    handleRemoveItem,
    calculateTotal
  } = useNewCart();
  const {
    data: zones
  } = useQuery({
    queryKey: ["delivery-zones"],
    queryFn: async () => {
      const {
        data,
        error
      } = await supabase.from("delivery_zones").select("*").eq("is_active", true).order("name");
      if (error) throw error;
      return data;
    }
  });
  const generatePDF = async () => {
    const doc = new jsPDF();
    const {
      data: {
        user
      }
    } = await supabase.auth.getUser();
    const {
      data: ownerData
    } = await supabase.from('owner_data').select('*').eq('owner_id', user?.id).single();

    // En-tête
    doc.setFontSize(20);
    doc.text("FACTURE", 105, 15, {
      align: "center"
    });

    // Informations de l'entreprise
    doc.setFontSize(12);
    doc.text(`${ownerData?.company_name || 'Entreprise'}`, 20, 30);

    // Informations du client (seulement si includeCustomerInfo est true)
    if (includeCustomerInfo) {
      doc.text("Client:", 20, 45);
      if (customerName) doc.text(`Nom: ${customerName}`, 30, 55);
      if (customerContact) doc.text(`Contact: ${customerContact}`, 30, 65);
      if (customerAddress) doc.text(`Adresse: ${customerAddress}`, 30, 75);
    }

    // Date et numéro de facture
    doc.text(`Date: ${new Date().toLocaleDateString()}`, 150, 45);
    doc.text(`N° Facture: ${Date.now()}`, 150, 55);

    // Tableau des produits
    const tableColumns = ["Produit", "Qté", "Prix unitaire", "Total"];
    const tableRows = cartItems.map(item => [item.stock_item.data.name, item.quantity.toString(), `${item.stock_item.data.price.toLocaleString()} FCFA`, `${(item.quantity * item.stock_item.data.price).toLocaleString()} FCFA`]);

    // @ts-ignore (jspdf-autotable n'a pas de types TS)
    doc.autoTable({
      startY: includeCustomerInfo ? 90 : 70,
      head: [tableColumns],
      body: tableRows
    });

    // Total et mode de paiement
    const finalY = (doc as any).lastAutoTable.finalY + 10;
    doc.text(`Mode de paiement: ${paymentMethod}`, 20, finalY);
    const total = cartItems.reduce((sum, item) => {
      return sum + item.quantity * item.stock_item.data.price;
    }, 0);
    doc.text(`Total: ${total.toLocaleString()} FCFA`, 150, finalY);

    // Génération et partage du PDF
    const pdfBlob = doc.output('blob');
    const pdfUrl = URL.createObjectURL(pdfBlob);

    // Télécharger le PDF
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = `facture_${Date.now()}.pdf`;
    link.click();

    // Partager sur WhatsApp (si disponible sur l'appareil)
    const whatsappText = `Voici votre facture de ${ownerData?.company_name || 'notre entreprise'}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(whatsappText)}`;
    window.open(whatsappUrl);
  };
  const {
    loading,
    handleValidateCart
  } = useCartValidation({
    cartItems,
    digitalSignature,
    isCredit,
    partialPayment,
    paymentDueDate,
    customerName,
    customerContact,
    customerAddress,
    paymentMethod,
    isDelivery,
    selectedZone,
    deliveryAddress,
    deliveryPaymentType,
    customDeliveryAmount,
    selectedDriver,
    customDriverName,
    onSuccess: async () => {
      if (generateInvoice) {
        await generatePDF();
      }
      onOpenChange(false);
      refetch();
    }
  });
  useEffect(() => {
    const checkAuth = async () => {
      const {
        data: {
          session
        }
      } = await supabase.auth.getSession();
      if (!session) {
        navigate('/login-owner');
      }
    };
    checkAuth();
  }, [navigate]);
  if (isLoading) {
    return <div>Chargement...</div>;
  }
  return <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
        <CartHeader />
        
        <div className="flex-1 overflow-y-auto min-h-0 py-4">
          <CartContent digitalSignature={digitalSignature} setDigitalSignature={setDigitalSignature} cartItems={cartItems} handleRemoveItem={handleRemoveItem} isCredit={isCredit} setIsCredit={setIsCredit} partialPayment={partialPayment} setPartialPayment={setPartialPayment} paymentDueDate={paymentDueDate} setPaymentDueDate={setPaymentDueDate} customerName={customerName} setCustomerName={setCustomerName} customerContact={customerContact} setCustomerContact={setCustomerContact} customerAddress={customerAddress} setCustomerAddress={setCustomerAddress} calculateTotal={calculateTotal} onPaymentMethodChange={setPaymentMethod} paymentMethod={paymentMethod} isDelivery={isDelivery} setIsDelivery={setIsDelivery} selectedZone={selectedZone} setSelectedZone={setSelectedZone} deliveryAddress={deliveryAddress} setDeliveryAddress={setDeliveryAddress} deliveryPaymentType={deliveryPaymentType} setDeliveryPaymentType={setDeliveryPaymentType} customDeliveryAmount={customDeliveryAmount} setCustomDeliveryAmount={setCustomDeliveryAmount} selectedDriver={selectedDriver} setSelectedDriver={setSelectedDriver} customDriverName={customDriverName} setCustomDriverName={setCustomDriverName} />

          <div className="p-4 border-t space-y-4 bg-slate-300">
            <div className="flex items-center space-x-2">
              <Switch id="invoice" checked={generateInvoice} onCheckedChange={setGenerateInvoice} className="data-[state=checked]:bg-violet-600 data-[state=unchecked]:bg-gray-200 hover:data-[state=checked]:bg-violet-500 hover:data-[state=unchecked]:bg-gray-300" />
              <Label htmlFor="invoice">Générer une facture après la vente</Label>
            </div>
            
            {generateInvoice && <>
                <div className="flex items-center space-x-2">
                  <Switch id="customer-info" checked={includeCustomerInfo} onCheckedChange={setIncludeCustomerInfo} className="data-[state=checked]:bg-violet-600 data-[state=unchecked]:bg-gray-200 hover:data-[state=checked]:bg-violet-500 hover:data-[state=unchecked]:bg-gray-300" />
                  <Label htmlFor="customer-info">
                    Inclure les informations du client sur la facture
                  </Label>
                </div>

                {includeCustomerInfo && <div className="space-y-4 mt-4 border rounded-lg p-4">
                    <h3 className="font-medium text-sm mb-2">
                      Informations du client pour la facture
                    </h3>
                    <div className="space-y-2">
                      <div>
                        <Label htmlFor="customerName">Nom du client</Label>
                        <Input id="customerName" value={customerName} onChange={e => setCustomerName(e.target.value)} placeholder="Nom du client" />
                      </div>
                      <div>
                        <Label htmlFor="customerContact">Contact</Label>
                        <Input id="customerContact" value={customerContact} onChange={e => setCustomerContact(e.target.value)} placeholder="Numéro de téléphone" />
                      </div>
                      <div>
                        <Label htmlFor="customerAddress">Adresse</Label>
                        <Input id="customerAddress" value={customerAddress} onChange={e => setCustomerAddress(e.target.value)} placeholder="Adresse du client" />
                      </div>
                    </div>
                  </div>}

                <p className="text-sm text-muted-foreground mt-2">
                  Une facture sera générée avec les informations de la vente. 
                  {includeCustomerInfo && " N'oubliez pas de saisir les coordonnées du client."}
                  Vous pourrez la télécharger et la partager via WhatsApp.
                </p>
              </>}
          </div>
        </div>

        <div className="border-t pt-4 mt-auto">
          <CartFooter calculateTotal={calculateTotal} loading={loading} cartItems={cartItems} digitalSignature={digitalSignature} paymentMethod={paymentMethod} onCancel={() => onOpenChange(false)} onValidate={handleValidateCart} isCredit={isCredit} partialPayment={partialPayment} />
        </div>
      </DialogContent>
    </Dialog>;
};